import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { 
  Settings, Mail, Calendar, Clock, 
  CheckCircle, AlertCircle, Send, Loader2, Download, FileSpreadsheet,
  User, Key, CalendarDays, Plus, Trash2, Save, Shield, Bell, Database, Building2, Copy, QrCode, Users, UserPlus, Edit, Lock, KeyRound, Scan, Smartphone, RefreshCw
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import * as faceapi from "face-api.js";
import PushNotificationToggle from "../components/PushNotificationToggle";

const SettingsPage = () => {
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processingMonthEnd, setProcessingMonthEnd] = useState(false);

  // Company info state
  const [companyInfo, setCompanyInfo] = useState(null);
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: "",
    address: "",
    phone: "",
    billing_email: ""
  });
  const [savingCompany, setSavingCompany] = useState(false);

  // Profile state
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Calendar state
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarData, setCalendarData] = useState({ holidays: [], company_holidays: [] });
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [savingCalendar, setSavingCalendar] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "", type: "national" });
  
  // Calendar click dialog state
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDateDialog, setShowDateDialog] = useState(false);
  const [dialogHolidayName, setDialogHolidayName] = useState("");
  const [dialogHolidayType, setDialogHolidayType] = useState("company");

  const [formData, setFormData] = useState({
    admin_email: "",
    work_start_time: "09:00",
    work_end_time: "18:00",
    standard_work_hours: 8,
    rounding_minutes: 15
  });

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    leave_request_notify_admin: true,
    leave_request_notify_employee: true,
    month_end_notify: true,
    attendance_anomaly_notify: true
  });
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [exportingData, setExportingData] = useState(false);

  const [monthEndData, setMonthEndData] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    send_email: true
  });

  const [downloadingTimecard, setDownloadingTimecard] = useState(false);

  // Staff management state
  const [staffList, setStaffList] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [showAddStaffDialog, setShowAddStaffDialog] = useState(false);
  const [showEditStaffDialog, setShowEditStaffDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({
    email: "",
    name: "",
    password: "",
    role: "manager",
    pin: ""
  });
  const [newPassword, setNewPassword] = useState("");
  const [newPin, setNewPin] = useState("");
  const [savingStaff, setSavingStaff] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Face descriptor batch processing
  const [processingDescriptors, setProcessingDescriptors] = useState(false);
  const [descriptorProgress, setDescriptorProgress] = useState({ current: 0, total: 0 });
  const [faceModelsLoaded, setFaceModelsLoaded] = useState(false);
  const [descriptorErrors, setDescriptorErrors] = useState([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchProfile();
    fetchCompanyInfo();
    fetchCalendar(calendarYear);
    fetchStaffList();
    fetchCurrentUser();
    loadFaceModels();
    
    // Check for Gmail callback
    if (searchParams.get("gmail") === "connected") {
      toast.success("Gmail連携が完了しました");
    }
  }, [searchParams]);

  const loadFaceModels = async () => {
    try {
      const MODEL_URL = "/models";
      await Promise.all([
        faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL).catch(() => null),
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
      ]);
      setFaceModelsLoaded(true);
    } catch (error) {
      console.error("Face model load error:", error);
    }
  };

  const computeDescriptorFromImage = async (base64Image) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = async () => {
        try {
          console.log("Image loaded, size:", img.width, "x", img.height);
          
          // 画像が小さすぎる場合は拡大
          let inputImage = img;
          if (img.width < 400 || img.height < 400) {
            const canvas = document.createElement('canvas');
            const scale = Math.max(400 / img.width, 400 / img.height);
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            inputImage = canvas;
            console.log("Image scaled to:", canvas.width, "x", canvas.height);
          }
          
          // TinyFaceDetectorを最初に試す（より軽量で広範囲検出）
          let detection = await faceapi
            .detectSingleFace(inputImage, new faceapi.TinyFaceDetectorOptions({ inputSize: 512, scoreThreshold: 0.4 }))
            .withFaceLandmarks()
            .withFaceDescriptor();
          
          // TinyFaceDetectorで失敗したらSsdMobilenetv1で再試行
          if (!detection) {
            console.log("Tiny failed, trying SSD...");
            detection = await faceapi
              .detectSingleFace(inputImage, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.4 }))
              .withFaceLandmarks()
              .withFaceDescriptor();
          }
          
          if (detection?.descriptor) {
            console.log("Descriptor computed successfully, confidence:", detection.detection?.score || "N/A");
            resolve(Array.from(detection.descriptor));
          } else {
            console.log("No face detected in image after both attempts");
            resolve(null);
          }
        } catch (e) {
          console.error("Descriptor computation error:", e);
          resolve(null);
        }
      };
      img.onerror = (e) => {
        console.error("Image load error:", e);
        resolve(null);
      };
      if (base64Image.startsWith("data:")) {
        img.src = base64Image;
      } else {
        img.src = `data:image/jpeg;base64,${base64Image}`;
      }
    });
  };

  const batchProcessDescriptors = async (reprocessAll = false) => {
    if (!faceModelsLoaded) {
      toast.error("顔認識モデルを読み込み中です。少々お待ちください。");
      return;
    }

    setProcessingDescriptors(true);
    setDescriptorErrors([]);  // エラーリストをリセット
    const token = localStorage.getItem("token");
    
    try {
      // Get faces to process (with optional reprocess_all flag)
      const res = await axios.get(`${API}/faces/without-descriptor?reprocess_all=${reprocessAll}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { faces } = res.data;
      if (faces.length === 0) {
        toast.success("すべての顔データに特徴量が設定済みです");
        setProcessingDescriptors(false);
        return;
      }

      setDescriptorProgress({ current: 0, total: faces.length });
      toast.info(`${faces.length}件の顔データを処理します...`);
      console.log(`Starting batch process for ${faces.length} faces`);

      let successCount = 0;
      let failCount = 0;
      const errors = [];

      for (let i = 0; i < faces.length; i++) {
        const face = faces[i];
        setDescriptorProgress({ current: i + 1, total: faces.length });
        
        console.log(`Processing ${i + 1}/${faces.length}: ${face.employee_name}`);
        
        if (!face.image) {
          console.log(`No image data for ${face.employee_name}`);
          errors.push({
            name: face.employee_name,
            reason: "画像データがありません"
          });
          failCount++;
          continue;
        }

        const descriptor = await computeDescriptorFromImage(face.image);
        
        if (descriptor) {
          try {
            await axios.post(`${API}/face-descriptor/update`, {
              employee_id: face.employee_id,
              face_id: face.face_id,
              descriptor: descriptor
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            successCount++;
            console.log(`Success: ${face.employee_name}`);
          } catch (e) {
            console.error(`API error for ${face.employee_name}:`, e);
            errors.push({
              name: face.employee_name,
              reason: "API保存エラー: " + (e.response?.data?.detail || e.message)
            });
            failCount++;
          }
        } else {
          console.log(`Failed to compute descriptor for ${face.employee_name}`);
          errors.push({
            name: face.employee_name,
            reason: "顔を検出できませんでした（画像が不鮮明、顔が小さい、または横向きの可能性）"
          });
          failCount++;
        }

        // Small delay to prevent UI blocking
        await new Promise(r => setTimeout(r, 100));
      }

      setDescriptorErrors(errors);
      
      if (failCount > 0) {
        toast.warning(`処理完了: ${successCount}件成功、${failCount}件失敗`);
        setShowErrorDialog(true);
      } else {
        toast.success(`処理完了: ${successCount}件すべて成功！`);
      }
      console.log(`Batch process complete: ${successCount} success, ${failCount} fail`);
    } catch (error) {
      toast.error("処理中にエラーが発生しました");
      console.error("Batch process error:", error);
    } finally {
      setProcessingDescriptors(false);
      setDescriptorProgress({ current: 0, total: 0 });
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setCompanyInfo(res.data.company);
      setCompanyForm({
        name: res.data.company?.name || "",
        address: res.data.company?.address || "",
        phone: res.data.company?.phone || "",
        billing_email: res.data.company?.billing_email || ""
      });
    } catch (error) {
      console.error("Company info fetch error:", error);
    }
  };

  const saveCompanyInfo = async () => {
    setSavingCompany(true);
    try {
      await axios.put(`${API}/company`, companyForm);
      toast.success("企業情報を更新しました");
      setEditingCompany(false);
      fetchCompanyInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || "更新に失敗しました");
    } finally {
      setSavingCompany(false);
    }
  };

  const copyCompanyCode = () => {
    if (companyInfo?.code) {
      navigator.clipboard.writeText(companyInfo.code);
      toast.success("企業コードをコピーしました");
    }
  };

  const copyFaceAuthUrl = () => {
    if (companyInfo?.code) {
      const url = `${window.location.origin}/face-auth?company=${companyInfo.code}`;
      navigator.clipboard.writeText(url);
      toast.success("認証URLをコピーしました");
    }
  };

  // Staff management functions
  const fetchCurrentUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setCurrentUser(res.data.user);
    } catch (error) {
      console.error("Current user fetch error:", error);
    }
  };

  const fetchStaffList = async () => {
    setLoadingStaff(true);
    try {
      const res = await axios.get(`${API}/staff`);
      setStaffList(res.data || []);
    } catch (error) {
      console.error("Staff list fetch error:", error);
    } finally {
      setLoadingStaff(false);
    }
  };

  const addStaff = async () => {
    if (!staffForm.email || !staffForm.name || !staffForm.password) {
      toast.error("全ての項目を入力してください");
      return;
    }
    if (staffForm.password.length < 8) {
      toast.error("パスワードは8文字以上で入力してください");
      return;
    }
    // PINのバリデーション（入力されている場合のみ）
    if (staffForm.pin && (!/^\d{4,6}$/.test(staffForm.pin))) {
      toast.error("暗証番号は4〜6桁の数字で入力してください");
      return;
    }
    setSavingStaff(true);
    try {
      const payload = { ...staffForm };
      if (!payload.pin) delete payload.pin;  // 空の場合は送信しない
      await axios.post(`${API}/staff`, payload);
      toast.success("スタッフを追加しました");
      setShowAddStaffDialog(false);
      setStaffForm({ email: "", name: "", password: "", role: "manager", pin: "" });
      fetchStaffList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "追加に失敗しました");
    } finally {
      setSavingStaff(false);
    }
  };

  const updateStaff = async () => {
    if (!selectedStaff) return;
    setSavingStaff(true);
    try {
      await axios.put(`${API}/staff/${selectedStaff.id}`, {
        name: staffForm.name,
        role: staffForm.role,
        is_active: selectedStaff.is_active
      });
      toast.success("スタッフ情報を更新しました");
      setShowEditStaffDialog(false);
      setSelectedStaff(null);
      fetchStaffList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "更新に失敗しました");
    } finally {
      setSavingStaff(false);
    }
  };

  const deleteStaff = async (staffId) => {
    if (!confirm("このスタッフを削除してもよろしいですか？")) return;
    try {
      await axios.delete(`${API}/staff/${staffId}`);
      toast.success("スタッフを削除しました");
      fetchStaffList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "削除に失敗しました");
    }
  };

  const resetPassword = async () => {
    if (!selectedStaff || !newPassword) return;
    if (newPassword.length < 8) {
      toast.error("パスワードは8文字以上で入力してください");
      return;
    }
    setSavingStaff(true);
    try {
      await axios.put(`${API}/staff/${selectedStaff.id}/password`, {
        new_password: newPassword
      });
      toast.success("パスワードをリセットしました");
      setShowPasswordDialog(false);
      setSelectedStaff(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "リセットに失敗しました");
    } finally {
      setSavingStaff(false);
    }
  };

  const toggleStaffActive = async (staff) => {
    try {
      await axios.put(`${API}/staff/${staff.id}`, {
        is_active: !staff.is_active
      });
      toast.success(staff.is_active ? "スタッフを無効化しました" : "スタッフを有効化しました");
      fetchStaffList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "更新に失敗しました");
    }
  };

  const openEditDialog = (staff) => {
    setSelectedStaff(staff);
    setStaffForm({
      email: staff.email,
      name: staff.name,
      password: "",
      role: staff.role,
      pin: ""
    });
    setShowEditStaffDialog(true);
  };

  const openPasswordDialog = (staff) => {
    setSelectedStaff(staff);
    setNewPassword("");
    setShowPasswordDialog(true);
  };

  const openPinDialog = (staff) => {
    setSelectedStaff(staff);
    setNewPin("");
    setShowPinDialog(true);
  };

  const updatePin = async () => {
    if (!selectedStaff) return;
    
    // PINのバリデーション（空の場合は削除）
    if (newPin && (!/^\d{4,6}$/.test(newPin))) {
      toast.error("暗証番号は4〜6桁の数字で入力してください");
      return;
    }
    
    setSavingStaff(true);
    try {
      await axios.put(`${API}/staff/${selectedStaff.id}/pin`, {
        pin: newPin || null
      });
      toast.success(newPin ? "暗証番号を設定しました" : "暗証番号を削除しました");
      setShowPinDialog(false);
      setSelectedStaff(null);
      setNewPin("");
      fetchStaffList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "更新に失敗しました");
    } finally {
      setSavingStaff(false);
    }
  };

  useEffect(() => {
    fetchCalendar(calendarYear);
  }, [calendarYear]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/profile`);
      setProfileData(prev => ({
        ...prev,
        name: res.data.name || "",
        email: res.data.email || ""
      }));
    } catch (error) {
      console.error("Profile fetch error:", error);
    }
  };

  const saveProfile = async () => {
    if (profileData.new_password && profileData.new_password !== profileData.confirm_password) {
      toast.error("新しいパスワードが一致しません");
      return;
    }
    setSavingProfile(true);
    try {
      const payload = { name: profileData.name };
      if (profileData.new_password && profileData.current_password) {
        payload.current_password = profileData.current_password;
        payload.new_password = profileData.new_password;
      }
      await axios.put(`${API}/profile`, payload);
      toast.success("プロフィールを更新しました");
      setProfileData(prev => ({ ...prev, current_password: "", new_password: "", confirm_password: "" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "更新に失敗しました");
    } finally {
      setSavingProfile(false);
    }
  };

  const fetchCalendar = async (year) => {
    setLoadingCalendar(true);
    try {
      const res = await axios.get(`${API}/calendar/${year}`);
      setCalendarData({
        holidays: res.data.holidays || [],
        company_holidays: res.data.company_holidays || []
      });
    } catch (error) {
      setCalendarData({ holidays: [], company_holidays: [] });
    } finally {
      setLoadingCalendar(false);
    }
  };

  const saveCalendar = async () => {
    setSavingCalendar(true);
    try {
      await axios.put(`${API}/calendar/${calendarYear}`, calendarData);
      toast.success("カレンダーを保存しました");
    } catch (error) {
      toast.error("保存に失敗しました");
    } finally {
      setSavingCalendar(false);
    }
  };

  const saveNotificationSettings = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(`${API}/notifications/settings`, notificationSettings);
      toast.success("通知設定を保存しました");
    } catch (error) {
      toast.error("保存に失敗しました");
    } finally {
      setSavingNotifications(false);
    }
  };

  const exportAllData = async () => {
    setExportingData(true);
    try {
      const res = await axios.get(`${API}/gdpr/export`);
      const dataStr = JSON.stringify(res.data, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `company_data_export_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("データをエクスポートしました");
    } catch (error) {
      toast.error("エクスポートに失敗しました");
    } finally {
      setExportingData(false);
    }
  };

  const addHoliday = () => {
    if (!newHoliday.date || !newHoliday.name) {
      toast.error("日付と名前を入力してください");
      return;
    }
    if (newHoliday.type === "national") {
      setCalendarData(prev => ({
        ...prev,
        holidays: [...prev.holidays, { date: newHoliday.date, name: newHoliday.name }]
      }));
    } else {
      setCalendarData(prev => ({
        ...prev,
        company_holidays: [...prev.company_holidays, { date: newHoliday.date, name: newHoliday.name }]
      }));
    }
    setNewHoliday({ date: "", name: "", type: "national" });
  };

  const removeHoliday = (date, type) => {
    if (type === "national") {
      setCalendarData(prev => ({
        ...prev,
        holidays: prev.holidays.filter(h => h.date !== date)
      }));
    } else {
      setCalendarData(prev => ({
        ...prev,
        company_holidays: prev.company_holidays.filter(h => h.date !== date)
      }));
    }
  };

  // 日本の祝日データ
  const getJapaneseHolidays = (year) => {
    const holidays = [
      { date: `${year}-01-01`, name: "元日" },
      { date: `${year}-01-13`, name: "成人の日" },
      { date: `${year}-02-11`, name: "建国記念の日" },
      { date: `${year}-02-23`, name: "天皇誕生日" },
      { date: `${year}-03-20`, name: "春分の日" },
      { date: `${year}-04-29`, name: "昭和の日" },
      { date: `${year}-05-03`, name: "憲法記念日" },
      { date: `${year}-05-04`, name: "みどりの日" },
      { date: `${year}-05-05`, name: "こどもの日" },
      { date: `${year}-07-21`, name: "海の日" },
      { date: `${year}-08-11`, name: "山の日" },
      { date: `${year}-09-16`, name: "敬老の日" },
      { date: `${year}-09-22`, name: "秋分の日" },
      { date: `${year}-10-14`, name: "スポーツの日" },
      { date: `${year}-11-03`, name: "文化の日" },
      { date: `${year}-11-23`, name: "勤労感謝の日" }
    ];
    return holidays;
  };

  // 祝日を一括追加
  const addAllJapaneseHolidays = () => {
    const holidays = getJapaneseHolidays(calendarYear);
    const existingDates = calendarData.holidays.map(h => h.date);
    const newHolidays = holidays.filter(h => !existingDates.includes(h.date));
    
    if (newHolidays.length === 0) {
      toast.info("すべての祝日は既に追加されています");
      return;
    }
    
    setCalendarData(prev => ({
      ...prev,
      holidays: [...prev.holidays, ...newHolidays]
    }));
    toast.success(`${newHolidays.length}件の祝日を追加しました`);
  };

  // 土日を一括で会社休業日に追加
  const addAllWeekends = () => {
    const weekends = [];
    const year = calendarYear;
    const existingDates = calendarData.company_holidays.map(h => h.date);
    
    for (let month = 0; month < 12; month++) {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dayOfWeek = date.getDay();
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          if (!existingDates.includes(dateStr)) {
            weekends.push({
              date: dateStr,
              name: dayOfWeek === 0 ? "日曜日" : "土曜日"
            });
          }
        }
      }
    }
    
    if (weekends.length === 0) {
      toast.info("すべての土日は既に追加されています");
      return;
    }
    
    setCalendarData(prev => ({
      ...prev,
      company_holidays: [...prev.company_holidays, ...weekends]
    }));
    toast.success(`${weekends.length}件の土日を追加しました`);
  };

  // 全ての休日をクリア
  const clearAllHolidays = (type) => {
    if (type === "national") {
      setCalendarData(prev => ({ ...prev, holidays: [] }));
      toast.success("祝日をすべて削除しました");
    } else {
      setCalendarData(prev => ({ ...prev, company_holidays: [] }));
      toast.success("会社休業日をすべて削除しました");
    }
  };

  // カレンダー表示用のデータを生成
  const getMonthCalendar = (month) => {
    const year = calendarYear;
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const date = new Date(year, month, day);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const nationalHoliday = calendarData.holidays.find(h => h.date === dateStr);
      const companyHoliday = calendarData.company_holidays.find(h => h.date === dateStr);
      const isHoliday = !!nationalHoliday || !!companyHoliday;
      days.push({ 
        day, 
        dateStr, 
        isWeekend, 
        isHoliday,
        holidayName: nationalHoliday?.name || companyHoliday?.name || null,
        holidayType: nationalHoliday ? 'national' : companyHoliday ? 'company' : null
      });
    }
    return days;
  };

  // 日付クリック時の処理 - 即座に休日をトグル
  const handleDateClick = (dayInfo) => {
    if (!dayInfo) return;
    
    if (dayInfo.isHoliday) {
      // 既に休日の場合は解除
      setCalendarData(prev => ({
        ...prev,
        holidays: prev.holidays.filter(h => h.date !== dayInfo.dateStr),
        company_holidays: prev.company_holidays.filter(h => h.date !== dayInfo.dateStr)
      }));
      toast.success(`${new Date(dayInfo.dateStr).toLocaleDateString('ja-JP')}の休日を解除しました`);
    } else {
      // 休日でない場合は休日に設定
      const date = new Date(dayInfo.dateStr);
      const dayOfWeek = date.getDay();
      let holidayName = "休業日";
      
      if (dayOfWeek === 0) {
        holidayName = "日曜日";
      } else if (dayOfWeek === 6) {
        holidayName = "土曜日";
      }
      
      const newEntry = { date: dayInfo.dateStr, name: holidayName };
      setCalendarData(prev => ({
        ...prev,
        company_holidays: [...prev.company_holidays, newEntry]
      }));
      toast.success(`${new Date(dayInfo.dateStr).toLocaleDateString('ja-JP')}を休日に設定しました`);
    }
  };
  
  // 日付長押し/右クリック時の処理 - ダイアログを表示
  const handleDateContextMenu = (e, dayInfo) => {
    if (!dayInfo) return;
    e.preventDefault();
    
    setSelectedDate(dayInfo);
    setShowDateDialog(true);
    
    if (dayInfo.holidayName) {
      setDialogHolidayName(dayInfo.holidayName);
      setDialogHolidayType(dayInfo.holidayType || "company");
    } else {
      const date = new Date(dayInfo.dateStr);
      const dayOfWeek = date.getDay();
      if (dayOfWeek === 0) {
        setDialogHolidayName("日曜日");
      } else if (dayOfWeek === 6) {
        setDialogHolidayName("土曜日");
      } else {
        setDialogHolidayName("休業日");
      }
      setDialogHolidayType("company");
    }
  };

  // 休日を追加
  const addHolidayFromDialog = () => {
    if (!selectedDate || !dialogHolidayName) {
      toast.error("名称を入力してください");
      return;
    }
    
    const newEntry = { date: selectedDate.dateStr, name: dialogHolidayName };
    
    if (dialogHolidayType === "national") {
      // 既存の祝日を削除してから追加
      setCalendarData(prev => ({
        ...prev,
        holidays: [...prev.holidays.filter(h => h.date !== selectedDate.dateStr), newEntry],
        company_holidays: prev.company_holidays.filter(h => h.date !== selectedDate.dateStr)
      }));
    } else {
      // 既存の会社休業日を削除してから追加
      setCalendarData(prev => ({
        ...prev,
        holidays: prev.holidays.filter(h => h.date !== selectedDate.dateStr),
        company_holidays: [...prev.company_holidays.filter(h => h.date !== selectedDate.dateStr), newEntry]
      }));
    }
    
    toast.success(`${new Date(selectedDate.dateStr).toLocaleDateString('ja-JP')}を休日に設定しました`);
    setShowDateDialog(false);
    setSelectedDate(null);
  };

  // 休日を削除
  const removeHolidayFromDialog = () => {
    if (!selectedDate) return;
    
    setCalendarData(prev => ({
      ...prev,
      holidays: prev.holidays.filter(h => h.date !== selectedDate.dateStr),
      company_holidays: prev.company_holidays.filter(h => h.date !== selectedDate.dateStr)
    }));
    
    toast.success(`${new Date(selectedDate.dateStr).toLocaleDateString('ja-JP')}の休日設定を解除しました`);
    setShowDateDialog(false);
    setSelectedDate(null);
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
      setFormData({
        admin_email: res.data.admin_email || "",
        work_start_time: res.data.work_start_time || "09:00",
        work_end_time: res.data.work_end_time || "18:00",
        standard_work_hours: res.data.standard_work_hours || 8,
        rounding_minutes: res.data.rounding_minutes || 15
      });
      // Load notification settings
      setNotificationSettings({
        leave_request_notify_admin: res.data.leave_request_notify_admin !== false,
        leave_request_notify_employee: res.data.leave_request_notify_employee !== false,
        month_end_notify: res.data.month_end_notify !== false,
        attendance_anomaly_notify: res.data.attendance_anomaly_notify !== false
      });
    } catch (error) {
      toast.error("設定の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, formData);
      toast.success("設定を保存しました");
      fetchSettings();
    } catch (error) {
      toast.error("設定の保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const connectGmail = async () => {
    try {
      const res = await axios.get(`${API}/oauth/gmail/login`);
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error("Gmail連携の開始に失敗しました");
    }
  };

  const processMonthEnd = async () => {
    if (!settings?.gmail_connected && monthEndData.send_email) {
      toast.error("メール送信するにはGmail連携が必要です");
      return;
    }

    if (monthEndData.send_email && !formData.admin_email) {
      toast.error("管理者メールアドレスを設定してください");
      return;
    }

    setProcessingMonthEnd(true);
    try {
      const res = await axios.post(`${API}/month-end`, monthEndData);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "月末処理に失敗しました");
    } finally {
      setProcessingMonthEnd(false);
    }
  };

  const downloadTimecardCSV = async () => {
    setDownloadingTimecard(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/attendance/export-timecard?year=${monthEndData.year}&month=${monthEndData.month}`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `timecard_${monthEndData.year}_${String(monthEndData.month).padStart(2, "0")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("タイムカードCSVをダウンロードしました");
    } catch (error) {
      toast.error("ダウンロードに失敗しました");
    } finally {
      setDownloadingTimecard(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-slate-900 dark:border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight mb-2">設定</h1>
        <p className="text-slate-600 dark:text-slate-400">システム設定と月末処理</p>
      </div>

      {/* Company Info Card - Full Width at Top */}
      <Card data-testid="company-info-card" className="border-emerald-200 dark:border-emerald-800">
        <CardHeader className="bg-emerald-50 dark:bg-emerald-900/30 rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-600" />
            企業情報
          </CardTitle>
          <CardDescription>
            企業の基本情報と認証用のアクセス情報
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {companyInfo ? (
            <div className="space-y-6">
              {/* Company Code - Prominent Display */}
              <div className="p-6 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-emerald-100 text-sm mb-1">企業コード</p>
                    <p className="text-4xl font-bold tracking-wider" data-testid="company-code-display">
                      {companyInfo.code}
                    </p>
                    <p className="text-emerald-100 text-sm mt-2">
                      従業員は認証時にこのコードを使用します
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={copyCompanyCode}
                      className="bg-white/20 hover:bg-white/30 text-white border-0"
                      data-testid="copy-company-code-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      コピー
                    </Button>
                  </div>
                </div>
              </div>

              {/* Face Auth URL */}
              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium mb-1">認証URL</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 truncate">
                      {window.location.origin}/face-auth?company={companyInfo.code}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyFaceAuthUrl}
                    className="ml-4 shrink-0"
                    data-testid="copy-face-auth-url-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    URLをコピー
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  このURLを従業員に共有するか、QRコードを作成してオフィスに掲示してください
                </p>
              </div>

              {/* Company Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>企業名</Label>
                  {editingCompany ? (
                    <Input
                      value={companyForm.name}
                      onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                      className="bg-white dark:bg-slate-800"
                    />
                  ) : (
                    <p className="p-2 bg-slate-50 dark:bg-slate-800 rounded">{companyInfo.name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>請求先メール</Label>
                  {editingCompany ? (
                    <Input
                      type="email"
                      value={companyForm.billing_email}
                      onChange={(e) => setCompanyForm({ ...companyForm, billing_email: e.target.value })}
                      className="bg-white dark:bg-slate-800"
                    />
                  ) : (
                    <p className="p-2 bg-slate-50 dark:bg-slate-800 rounded">{companyInfo.billing_email || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>住所</Label>
                  {editingCompany ? (
                    <Input
                      value={companyForm.address}
                      onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                      className="bg-white dark:bg-slate-800"
                    />
                  ) : (
                    <p className="p-2 bg-slate-50 dark:bg-slate-800 rounded">{companyInfo.address || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>電話番号</Label>
                  {editingCompany ? (
                    <Input
                      value={companyForm.phone}
                      onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                      className="bg-white dark:bg-slate-800"
                    />
                  ) : (
                    <p className="p-2 bg-slate-50 dark:bg-slate-800 rounded">{companyInfo.phone || "-"}</p>
                  )}
                </div>
              </div>

              {/* Plan & Status Info */}
              <div className="flex gap-4 pt-2">
                <Badge variant="outline" className="text-sm">
                  プラン: {companyInfo.plan_name || "未設定"}
                </Badge>
                <Badge 
                  className={`text-sm ${
                    companyInfo.status === "active" ? "bg-emerald-600" :
                    companyInfo.status === "trial" ? "bg-blue-600" :
                    "bg-slate-600"
                  }`}
                >
                  {companyInfo.status === "active" ? "有効" :
                   companyInfo.status === "trial" ? "トライアル" :
                   companyInfo.status}
                </Badge>
              </div>

              {/* Edit Buttons */}
              <div className="flex gap-2 pt-2">
                {editingCompany ? (
                  <>
                    <Button onClick={saveCompanyInfo} disabled={savingCompany}>
                      {savingCompany ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      保存
                    </Button>
                    <Button variant="outline" onClick={() => setEditingCompany(false)}>
                      キャンセル
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" onClick={() => setEditingCompany(true)}>
                    企業情報を編集
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Staff Management Card */}
      {currentUser?.role === "admin" && (
        <Card data-testid="staff-management-card" className="border-blue-200 dark:border-blue-800">
          <CardHeader className="bg-blue-50 dark:bg-blue-900/30 rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              管理スタッフ
            </CardTitle>
            <CardDescription>
              システムにアクセスできる管理スタッフを管理
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Add Staff Button */}
              <div className="flex justify-end">
                <Button onClick={() => {
                  setStaffForm({ email: "", name: "", password: "", role: "manager", pin: "" });
                  setShowAddStaffDialog(true);
                }} className="bg-blue-600 hover:bg-blue-700">
                  <UserPlus className="w-4 h-4 mr-2" />
                  スタッフを追加
                </Button>
              </div>

              {/* Staff List */}
              {loadingStaff ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : staffList.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">名前</th>
                        <th className="text-left p-3 text-sm font-medium">メールアドレス</th>
                        <th className="text-center p-3 text-sm font-medium">権限</th>
                        <th className="text-center p-3 text-sm font-medium">暗証番号</th>
                        <th className="text-center p-3 text-sm font-medium">状態</th>
                        <th className="text-center p-3 text-sm font-medium">操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staffList.map((staff) => (
                        <tr key={staff.id} className="border-t">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-blue-600 font-medium">
                                {staff.name?.charAt(0) || "?"}
                              </div>
                              <span className="font-medium">{staff.name}</span>
                              {staff.id === currentUser?.id && (
                                <Badge variant="outline" className="text-xs">自分</Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-slate-600 dark:text-slate-400">{staff.email}</td>
                          <td className="p-3 text-center">
                            <Badge className={staff.role === "admin" ? "bg-purple-600" : "bg-blue-600"}>
                              {staff.role === "admin" ? "管理者" : "マネージャー"}
                            </Badge>
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={staff.has_pin ? "bg-emerald-600" : "bg-slate-400"}>
                              {staff.has_pin ? "設定済" : "未設定"}
                            </Badge>
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={staff.is_active !== false ? "bg-emerald-600" : "bg-slate-400"}>
                              {staff.is_active !== false ? "有効" : "無効"}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="flex justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(staff)}
                                title="編集"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openPasswordDialog(staff)}
                                title="パスワードリセット"
                              >
                                <Lock className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openPinDialog(staff)}
                                title="暗証番号設定"
                                className={staff.has_pin ? "text-emerald-600" : "text-slate-400"}
                              >
                                <KeyRound className="w-4 h-4" />
                              </Button>
                              {staff.id !== currentUser?.id && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleStaffActive(staff)}
                                    title={staff.is_active !== false ? "無効化" : "有効化"}
                                  >
                                    <Shield className={`w-4 h-4 ${staff.is_active !== false ? "text-amber-500" : "text-emerald-500"}`} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteStaff(staff.id)}
                                    className="text-red-500 hover:text-red-600"
                                    title="削除"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>スタッフが登録されていません</p>
                </div>
              )}

              {/* Role Description */}
              <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg text-sm">
                <p className="font-medium mb-2">権限について</p>
                <ul className="space-y-1 text-slate-600 dark:text-slate-400">
                  <li><span className="text-purple-600 font-medium">管理者:</span> 全ての機能にアクセス可能（スタッフ管理を含む）</li>
                  <li><span className="text-blue-600 font-medium">マネージャー:</span> スタッフ管理以外の機能にアクセス可能</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Gmail Integration */}
        <Card data-testid="gmail-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Gmail連携
            </CardTitle>
            <CardDescription>
              月末処理で自動メール送信を行うためにGmailアカウントを連携します
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800">
              <div className="flex items-center gap-3">
                {settings?.gmail_connected ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="font-medium">連携済み</p>
                      <p className="text-sm text-slate-500">{settings.gmail_email}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="font-medium">未連携</p>
                      <p className="text-sm text-slate-500">Gmailアカウントを連携してください</p>
                    </div>
                  </>
                )}
              </div>
              {settings?.gmail_connected ? (
                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">
                  接続済
                </Badge>
              ) : (
                <Button onClick={connectGmail} data-testid="connect-gmail-btn">
                  連携する
                </Button>
              )}
            </div>

            {/* Notification Settings */}
            {settings?.gmail_connected && (
              <div className="pt-4 border-t space-y-4">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  通知設定
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">有給申請時の管理者通知</p>
                      <p className="text-xs text-slate-500">従業員が有給を申請したときに通知を受け取る</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={notificationSettings.leave_request_notify_admin}
                      onChange={(e) => setNotificationSettings(prev => ({ ...prev, leave_request_notify_admin: e.target.checked }))}
                      className="w-5 h-5 rounded border-slate-300"
                      data-testid="notify-admin-checkbox"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">承認/却下時の従業員通知</p>
                      <p className="text-xs text-slate-500">有給申請が承認または却下されたときに従業員に通知</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={notificationSettings.leave_request_notify_employee}
                      onChange={(e) => setNotificationSettings(prev => ({ ...prev, leave_request_notify_employee: e.target.checked }))}
                      className="w-5 h-5 rounded border-slate-300"
                      data-testid="notify-employee-checkbox"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">月末処理完了通知</p>
                      <p className="text-xs text-slate-500">月末の勤怠データ処理完了時に通知</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={notificationSettings.month_end_notify}
                      onChange={(e) => setNotificationSettings(prev => ({ ...prev, month_end_notify: e.target.checked }))}
                      className="w-5 h-5 rounded border-slate-300"
                      data-testid="notify-monthend-checkbox"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">勤怠異常アラート</p>
                      <p className="text-xs text-slate-500">打刻漏れなどの異常を検知した時に通知</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={notificationSettings.attendance_anomaly_notify}
                      onChange={(e) => setNotificationSettings(prev => ({ ...prev, attendance_anomaly_notify: e.target.checked }))}
                      className="w-5 h-5 rounded border-slate-300"
                      data-testid="notify-anomaly-checkbox"
                    />
                  </div>
                </div>
                <Button 
                  onClick={saveNotificationSettings} 
                  disabled={savingNotifications}
                  size="sm"
                  className="w-full"
                  data-testid="save-notifications-btn"
                >
                  {savingNotifications ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  通知設定を保存
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* General Settings */}
        <Card data-testid="general-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              基本設定
            </CardTitle>
            <CardDescription>
              勤務時間と管理者メールアドレスを設定します
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin_email">管理者メールアドレス</Label>
              <Input
                id="admin_email"
                type="email"
                value={formData.admin_email}
                onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                placeholder="admin@example.com"
                data-testid="admin-email-input"
              />
              <p className="text-xs text-slate-500">月末処理のCSVファイルがこのアドレスに送信されます</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="work_start">始業時刻</Label>
                <Input
                  id="work_start"
                  type="time"
                  value={formData.work_start_time}
                  onChange={(e) => setFormData({ ...formData, work_start_time: e.target.value })}
                  data-testid="work-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="work_end">終業時刻</Label>
                <Input
                  id="work_end"
                  type="time"
                  value={formData.work_end_time}
                  onChange={(e) => setFormData({ ...formData, work_end_time: e.target.value })}
                  data-testid="work-end-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="standard_hours">標準労働時間（時間）</Label>
              <Input
                id="standard_hours"
                type="number"
                value={formData.standard_work_hours}
                onChange={(e) => setFormData({ ...formData, standard_work_hours: parseFloat(e.target.value) })}
                data-testid="standard-hours-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rounding_minutes">打刻の丸め単位</Label>
              <Select
                value={String(formData.rounding_minutes)}
                onValueChange={(v) => setFormData({ ...formData, rounding_minutes: parseInt(v) })}
              >
                <SelectTrigger id="rounding_minutes" data-testid="rounding-minutes-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1" data-testid="rounding-opt-1">丸めなし（1分）</SelectItem>
                  <SelectItem value="5" data-testid="rounding-opt-5">5分刻み</SelectItem>
                  <SelectItem value="10" data-testid="rounding-opt-10">10分刻み</SelectItem>
                  <SelectItem value="15" data-testid="rounding-opt-15">15分刻み</SelectItem>
                  <SelectItem value="30" data-testid="rounding-opt-30">30分刻み</SelectItem>
                  <SelectItem value="60" data-testid="rounding-opt-60">60分刻み</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">出勤は切り捨て、退勤は切り上げで丸めます（実際の打刻時刻も別途記録されます）</p>
            </div>
            <Button onClick={saveSettings} disabled={saving} className="w-full" data-testid="save-settings-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  保存中...
                </>
              ) : (
                "設定を保存"
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Admin Profile Settings */}
        <Card data-testid="profile-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              管理者情報
            </CardTitle>
            <CardDescription>
              管理者アカウントの名前とパスワードを変更できます
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="profile_name">名前</Label>
              <Input
                id="profile_name"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                placeholder="管理者名"
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile_email">メールアドレス</Label>
              <Input
                id="profile_email"
                type="email"
                value={profileData.email}
                disabled
                className="bg-slate-100 dark:bg-slate-800"
                data-testid="profile-email-input"
              />
              <p className="text-xs text-slate-500">※ メールアドレスは変更できません</p>
            </div>
            <div className="pt-2 border-t">
              <Label className="text-sm font-medium mb-3 block">パスワード変更</Label>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="current_password" className="text-xs text-slate-500">現在のパスワード</Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={profileData.current_password}
                    onChange={(e) => setProfileData({ ...profileData, current_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="current-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_password" className="text-xs text-slate-500">新しいパスワード</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={profileData.new_password}
                    onChange={(e) => setProfileData({ ...profileData, new_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password" className="text-xs text-slate-500">パスワード確認</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={profileData.confirm_password}
                    onChange={(e) => setProfileData({ ...profileData, confirm_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
            </div>
            <Button onClick={saveProfile} disabled={savingProfile} className="w-full" data-testid="save-profile-btn">
              {savingProfile ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  保存中...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  プロフィールを保存
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Month End Processing */}
        <Card className="lg:col-span-2" data-testid="month-end-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              月末締め処理
            </CardTitle>
            <CardDescription>
              指定した月の勤怠データを確定し、CSVファイルを生成・送信します
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>年</Label>
                <Select 
                  value={monthEndData.year.toString()} 
                  onValueChange={(v) => setMonthEndData({ ...monthEndData, year: parseInt(v) })}
                >
                  <SelectTrigger data-testid="month-end-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2023, 2024, 2025, 2026].map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}年</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>月</Label>
                <Select 
                  value={monthEndData.month.toString()} 
                  onValueChange={(v) => setMonthEndData({ ...monthEndData, month: parseInt(v) })}
                >
                  <SelectTrigger data-testid="month-end-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <SelectItem key={month} value={month.toString()}>{month}月</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>メール送信</Label>
                <div className="flex items-center gap-3 h-10">
                  <Switch
                    checked={monthEndData.send_email}
                    onCheckedChange={(v) => setMonthEndData({ ...monthEndData, send_email: v })}
                    data-testid="send-email-switch"
                  />
                  <span className="text-sm text-slate-500">
                    {monthEndData.send_email ? "送信する" : "送信しない"}
                  </span>
                </div>
              </div>
            </div>

            {monthEndData.send_email && !settings?.gmail_connected && (
              <div className="p-4 rounded-xl bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/30">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    メール送信するにはGmail連携が必要です
                  </p>
                </div>
              </div>
            )}

            <Button 
              onClick={processMonthEnd} 
              disabled={processingMonthEnd}
              className="w-full btn-primary py-6"
              data-testid="process-month-end-btn"
            >
              {processingMonthEnd ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  処理中...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {monthEndData.year}年{monthEndData.month}月の締め処理を実行
                </>
              )}
            </Button>

            <div className="border-t border-slate-200 dark:border-slate-700 pt-6 mt-2">
              <h4 className="font-medium mb-4 flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4" />
                給料らくだPRO26形式 タイムカードCSV
              </h4>
              <p className="text-sm text-slate-500 mb-4">
                給料らくだPRO26にインポート可能な21列形式のタイムカードCSVをダウンロードします。
              </p>
              <Button 
                onClick={downloadTimecardCSV}
                disabled={downloadingTimecard}
                variant="outline"
                className="w-full py-5"
                data-testid="download-timecard-btn"
              >
                {downloadingTimecard ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ダウンロード中...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    タイムカードCSVをダウンロード
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Company Calendar */}
      <Card data-testid="calendar-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            年間カレンダー
          </CardTitle>
          <CardDescription>
            祝日と会社休業日を登録して勤怠計算に反映させます
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <Label>年度</Label>
            <Select 
              value={calendarYear.toString()} 
              onValueChange={(v) => setCalendarYear(parseInt(v))}
            >
              <SelectTrigger className="w-32" data-testid="calendar-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}年</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {loadingCalendar && <Loader2 className="w-4 h-4 animate-spin" />}
          </div>

          {/* Quick Actions */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl">
            <Button
              onClick={addAllJapaneseHolidays}
              variant="outline"
              className="bg-white dark:bg-slate-800 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
              data-testid="add-all-holidays-btn"
            >
              <Calendar className="w-4 h-4 mr-2 text-red-500" />
              祝日を一括追加
            </Button>
            <Button
              onClick={addAllWeekends}
              variant="outline"
              className="bg-white dark:bg-slate-800 border-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              data-testid="add-all-weekends-btn"
            >
              <CalendarDays className="w-4 h-4 mr-2 text-blue-500" />
              土日を一括追加
            </Button>
            <Button
              onClick={() => clearAllHolidays("national")}
              variant="outline"
              className="bg-white dark:bg-slate-800 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
              data-testid="clear-holidays-btn"
            >
              <Trash2 className="w-4 h-4 mr-2 text-red-500" />
              祝日をクリア
            </Button>
            <Button
              onClick={() => clearAllHolidays("company")}
              variant="outline"
              className="bg-white dark:bg-slate-800 border-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              data-testid="clear-company-holidays-btn"
            >
              <Trash2 className="w-4 h-4 mr-2 text-blue-500" />
              休業日をクリア
            </Button>
          </div>

          {/* Visual Calendar - Clickable */}
          <div className="border rounded-xl p-4 bg-white dark:bg-slate-900">
            <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {calendarYear}年 カレンダー
            </h4>
            <p className="text-xs text-slate-500 mb-4">クリックで休日設定/解除 ・ 右クリックで詳細編集</p>
            <div className="grid grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 12 }, (_, month) => (
                <div key={month} className="border rounded-lg p-2 bg-slate-50 dark:bg-slate-800">
                  <p className="text-xs font-medium text-center mb-2 text-slate-700 dark:text-slate-300">
                    {month + 1}月
                  </p>
                  <div className="grid grid-cols-7 gap-px text-center">
                    {['日', '月', '火', '水', '木', '金', '土'].map(day => (
                      <div key={day} className={`text-[8px] ${day === '日' ? 'text-red-500' : day === '土' ? 'text-blue-500' : 'text-slate-400'}`}>
                        {day}
                      </div>
                    ))}
                    {getMonthCalendar(month).map((dayInfo, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleDateClick(dayInfo)}
                        onContextMenu={(e) => handleDateContextMenu(e, dayInfo)}
                        title={dayInfo?.holidayName || (dayInfo ? 'クリックで休日設定' : '')}
                        className={`text-[9px] h-5 flex items-center justify-center rounded-sm cursor-pointer transition-all select-none
                          ${!dayInfo ? '' : 
                            dayInfo.isHoliday ? 'bg-red-500 text-white font-bold hover:bg-red-600' :
                            dayInfo.isWeekend ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 hover:bg-blue-200 dark:hover:bg-blue-800/50' :
                            'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                          }`}
                      >
                        {dayInfo?.day || ''}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-4 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
                <span>休日（クリックで解除）</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-100 dark:bg-blue-900/30 rounded-sm border"></div>
                <span>土日（未設定）</span>
              </div>
            </div>
          </div>

          {/* Date Click Dialog */}
          {showDateDialog && selectedDate && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDateDialog(false)}>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-emerald-500" />
                  {new Date(selectedDate.dateStr).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
                </h3>
                
                {selectedDate.isHoliday ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-red-50 dark:bg-red-500/10 rounded-lg">
                      <p className="text-sm font-medium text-red-700 dark:text-red-300">
                        現在の設定: {selectedDate.holidayName}
                      </p>
                      <p className="text-xs text-red-600 dark:text-red-400">
                        種別: {selectedDate.holidayType === 'national' ? '祝日' : '会社休業日'}
                      </p>
                    </div>
                    
                    <div className="border-t pt-4 space-y-3">
                      <p className="text-sm text-slate-600 dark:text-slate-400">設定を変更:</p>
                      <div className="space-y-2">
                        <Label className="text-xs">名称</Label>
                        <Input
                          value={dialogHolidayName}
                          onChange={(e) => setDialogHolidayName(e.target.value)}
                          placeholder="休日の名称"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">種別</Label>
                        <Select value={dialogHolidayType} onValueChange={setDialogHolidayType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="national">祝日</SelectItem>
                            <SelectItem value="company">会社休業日</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Button onClick={addHolidayFromDialog} className="flex-1">
                        <Save className="w-4 h-4 mr-2" />
                        更新
                      </Button>
                      <Button onClick={removeHolidayFromDialog} variant="destructive" className="flex-1">
                        <Trash2 className="w-4 h-4 mr-2" />
                        休日解除
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      この日を休日として設定しますか？
                    </p>
                    
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label className="text-xs">名称</Label>
                        <Input
                          value={dialogHolidayName}
                          onChange={(e) => setDialogHolidayName(e.target.value)}
                          placeholder="例: 創立記念日、夏季休暇"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">種別</Label>
                        <Select value={dialogHolidayType} onValueChange={setDialogHolidayType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="national">祝日</SelectItem>
                            <SelectItem value="company">会社休業日</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Button onClick={addHolidayFromDialog} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                        <Plus className="w-4 h-4 mr-2" />
                        休日に設定
                      </Button>
                      <Button onClick={() => setShowDateDialog(false)} variant="outline" className="flex-1">
                        キャンセル
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {/* National Holidays */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4 text-red-500" />
                祝日 ({calendarData.holidays.length}件)
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {calendarData.holidays.length === 0 ? (
                  <p className="text-sm text-slate-500 py-2">登録された祝日はありません</p>
                ) : (
                  calendarData.holidays
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map(h => (
                      <div key={h.date} className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-500/10 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{h.name}</p>
                          <p className="text-xs text-slate-500">{new Date(h.date).toLocaleDateString('ja-JP')}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeHoliday(h.date, "national")} className="h-7 text-red-500 hover:text-red-600">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))
                )}
              </div>
            </div>

            {/* Company Holidays */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-blue-500" />
                会社休業日 ({calendarData.company_holidays.length}件)
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {calendarData.company_holidays.length === 0 ? (
                  <p className="text-sm text-slate-500 py-2">登録された会社休業日はありません</p>
                ) : (
                  calendarData.company_holidays
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map(h => (
                      <div key={h.date} className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-500/10 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{h.name}</p>
                          <p className="text-xs text-slate-500">{new Date(h.date).toLocaleDateString('ja-JP')}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeHoliday(h.date, "company")} className="h-7 text-blue-500 hover:text-blue-600">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>

          {/* Add Holiday */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-sm mb-3">新規追加</h4>
            <div className="grid sm:grid-cols-4 gap-3">
              <Input
                type="date"
                value={newHoliday.date}
                onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                data-testid="holiday-date-input"
              />
              <Input
                placeholder="名称（例: 元日）"
                value={newHoliday.name}
                onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                data-testid="holiday-name-input"
              />
              <Select value={newHoliday.type} onValueChange={(v) => setNewHoliday({ ...newHoliday, type: v })}>
                <SelectTrigger data-testid="holiday-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="national">祝日</SelectItem>
                  <SelectItem value="company">会社休業日</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={addHoliday} data-testid="add-holiday-btn">
                <Plus className="w-4 h-4 mr-1" />
                追加
              </Button>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={saveCalendar} disabled={savingCalendar} className="px-8" data-testid="save-calendar-btn">
              {savingCalendar ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  保存中...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  カレンダーを保存
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* GDPR / Data Management */}
      <Card data-testid="gdpr-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            データ管理 (GDPR対応)
          </CardTitle>
          <CardDescription>
            データのエクスポートと削除に関する設定
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-500/10 rounded-lg">
            <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
              <Database className="w-4 h-4" />
              データポータビリティ
            </h4>
            <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">
              GDPR第20条に基づき、すべての会社データをJSON形式でエクスポートできます。
              エクスポートされるデータには、従業員情報、勤怠記録、有給申請履歴、カレンダー設定が含まれます。
            </p>
            <Button 
              onClick={exportAllData} 
              disabled={exportingData}
              variant="outline"
              className="w-full"
              data-testid="export-all-data-btn"
            >
              {exportingData ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  エクスポート中...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  全データをエクスポート
                </>
              )}
            </Button>
          </div>

          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <h4 className="font-medium text-sm mb-2">データ削除について</h4>
            <p className="text-xs text-slate-600 dark:text-slate-400">
              GDPR第17条「忘れられる権利」に基づき、従業員の個人データを完全に削除または匿名化できます。
              従業員管理ページから個別に操作してください。
            </p>
          </div>

          <div className="p-4 bg-yellow-50 dark:bg-yellow-500/10 rounded-lg border border-yellow-200 dark:border-yellow-500/20">
            <h4 className="font-medium text-sm mb-2 text-yellow-800 dark:text-yellow-400">注意事項</h4>
            <ul className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1">
              <li>• データ削除は元に戻せません</li>
              <li>• 法的保存義務のあるデータは削除前に確認してください</li>
              <li>• 匿名化を選択すると、統計データは保持されます</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* PWA / アプリ設定 */}
      <Card data-testid="pwa-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            アプリ設定
          </CardTitle>
          <CardDescription>
            スマートフォンアプリとしての設定
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg">
            <h4 className="font-medium text-sm mb-2">ホーム画面への追加</h4>
            <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">
              このアプリをスマートフォンのホーム画面に追加すると、ネイティブアプリのように素早く起動できます。
            </p>
            <div className="text-xs text-slate-500 space-y-1">
              <p><strong>iPhone:</strong> Safari で共有ボタン → 「ホーム画面に追加」</p>
              <p><strong>Android:</strong> Chrome でメニュー → 「ホーム画面に追加」</p>
            </div>
          </div>
          
          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <h4 className="font-medium text-sm mb-3">プッシュ通知</h4>
            <PushNotificationToggle />
            <p className="text-xs text-slate-500 mt-2">
              打刻リマインダーや重要なお知らせを受け取れます
            </p>
          </div>
          
          <div className="p-4 bg-blue-50 dark:bg-blue-500/10 rounded-lg">
            <h4 className="font-medium text-sm mb-2">オフライン対応</h4>
            <p className="text-xs text-slate-600 dark:text-slate-400">
              認証画面はオフラインでも動作します。インターネット接続がなくても打刻可能で、
              オンライン復帰時に自動的にデータが同期されます。
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            15分刻み計算について
          </h3>
          <div className="grid sm:grid-cols-2 gap-6 text-sm text-slate-600 dark:text-slate-400">
            <div>
              <p className="font-medium text-slate-900 dark:text-white mb-2">出勤時刻（切り捨て）</p>
              <ul className="space-y-1">
                <li>10:05 → 10:00</li>
                <li>10:14 → 10:00</li>
                <li>10:15 → 10:15</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-slate-900 dark:text-white mb-2">退勤時刻（切り上げ）</p>
              <ul className="space-y-1">
                <li>18:01 → 18:15</li>
                <li>18:15 → 18:15</li>
                <li>18:16 → 18:30</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Staff Dialog */}
      <Dialog open={showAddStaffDialog} onOpenChange={setShowAddStaffDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              スタッフを追加
            </DialogTitle>
            <DialogDescription>
              新しい管理スタッフのアカウントを作成します
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>名前 <span className="text-red-500">*</span></Label>
              <Input
                value={staffForm.name}
                onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                placeholder="山田 太郎"
              />
            </div>
            <div className="space-y-2">
              <Label>メールアドレス <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={staffForm.email}
                onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                placeholder="yamada@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>パスワード <span className="text-red-500">*</span></Label>
              <Input
                type="password"
                value={staffForm.password}
                onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })}
                placeholder="8文字以上"
              />
            </div>
            <div className="space-y-2">
              <Label>権限</Label>
              <Select value={staffForm.role} onValueChange={(v) => setStaffForm({ ...staffForm, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">マネージャー</SelectItem>
                  <SelectItem value="admin">管理者</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>暗証番号（任意）</Label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={staffForm.pin}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setStaffForm({ ...staffForm, pin: value });
                }}
                placeholder="4〜6桁の数字"
              />
              <p className="text-xs text-slate-500">暗証番号は後から設定することもできます</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStaffDialog(false)}>
              キャンセル
            </Button>
            <Button onClick={addStaff} disabled={savingStaff} className="bg-blue-600 hover:bg-blue-700">
              {savingStaff ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              追加
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Dialog */}
      <Dialog open={showEditStaffDialog} onOpenChange={setShowEditStaffDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              スタッフを編集
            </DialogTitle>
            <DialogDescription>
              {selectedStaff?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>名前</Label>
              <Input
                value={staffForm.name}
                onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>権限</Label>
              <Select 
                value={staffForm.role} 
                onValueChange={(v) => setStaffForm({ ...staffForm, role: v })}
                disabled={selectedStaff?.id === currentUser?.id}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">マネージャー</SelectItem>
                  <SelectItem value="admin">管理者</SelectItem>
                </SelectContent>
              </Select>
              {selectedStaff?.id === currentUser?.id && (
                <p className="text-xs text-amber-500">自分の権限は変更できません</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditStaffDialog(false)}>
              キャンセル
            </Button>
            <Button onClick={updateStaff} disabled={savingStaff} className="bg-blue-600 hover:bg-blue-700">
              {savingStaff ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" />
              パスワードリセット
            </DialogTitle>
            <DialogDescription>
              {selectedStaff?.name}（{selectedStaff?.email}）
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>新しいパスワード <span className="text-red-500">*</span></Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="8文字以上"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
              キャンセル
            </Button>
            <Button onClick={resetPassword} disabled={savingStaff} className="bg-amber-600 hover:bg-amber-700">
              {savingStaff ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              リセット
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN Setting Dialog */}
      <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-emerald-600" />
              暗証番号設定
            </DialogTitle>
            <DialogDescription>
              {selectedStaff?.name}（{selectedStaff?.email}）
              {selectedStaff?.has_pin && (
                <span className="ml-2 text-emerald-600 text-xs">現在設定済</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>暗証番号</Label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={newPin}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setNewPin(value);
                }}
                placeholder="4〜6桁の数字"
              />
              <p className="text-xs text-slate-500">
                空欄で保存すると暗証番号を削除します
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPinDialog(false)}>
              キャンセル
            </Button>
            <Button onClick={updatePin} disabled={savingStaff} className="bg-emerald-600 hover:bg-emerald-700">
              {savingStaff ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
              {newPin ? "設定" : "削除"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Descriptor Processing Error Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              特徴量計算エラー
            </DialogTitle>
            <DialogDescription>
              以下の従業員の顔写真で特徴量を計算できませんでした。
              写真を撮り直すか、より鮮明な正面写真に差し替えてください。
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            {descriptorErrors.length > 0 ? (
              <div className="space-y-2">
                {descriptorErrors.map((error, index) => (
                  <div 
                    key={index}
                    className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-red-800 dark:text-red-200">
                          {error.name}
                        </p>
                        <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                          {error.reason}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500">エラーはありません</p>
            )}
          </div>
          <DialogFooter className="border-t pt-4">
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <p className="text-xs text-slate-500 flex-1">
                {descriptorErrors.length}件のエラーが発生しました
              </p>
              <Button onClick={() => setShowErrorDialog(false)}>
                閉じる
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
